import { Link } from 'react-router-dom';
import { ArrowRight, Building2, UtensilsCrossed, Plane, ChefHat, Building, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/shared/SEO';

export default function IndustriesPage() {
  const industries = [
    {
      id: 'hotels',
      name: 'Hotels & Resorts',
      icon: Building2,
      description: 'From budget hotels to luxury resorts, we supply complete hospitality solutions including guest amenities, toiletries, housekeeping supplies, and eco-friendly packaging.',
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      products: ['Guest Amenities', 'Toiletries', 'Housekeeping Supplies', 'Paper Products', 'Safety Items'],
      stats: { clients: '200+', years: '15+' }
    },
    {
      id: 'restaurants',
      name: 'Restaurants & Cafes',
      icon: UtensilsCrossed,
      description: 'Quality food packaging, takeaway containers, and dining essentials for fine dining restaurants, quick service outlets, and cafes.',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800',
      products: ['Food Packaging', 'Takeaway Containers', 'Napkins', 'Cutlery', 'Cleaning Supplies'],
      stats: { clients: '150+', years: '12+' }
    },
    {
      id: 'airlines',
      name: 'Airlines & Aviation',
      icon: Plane,
      description: 'In-flight amenity kits, meal packaging, and disposable products that meet aviation industry standards and regulations.',
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800',
      products: ['Amenity Kits', 'Meal Trays', 'Cutlery Sets', 'Hygiene Products', 'Packaging'],
      stats: { clients: '10+', years: '8+' }
    },
    {
      id: 'catering',
      name: 'Catering Services',
      icon: ChefHat,
      description: 'Bulk supplies for event catering, corporate catering, and outdoor catering services with focus on eco-friendly options.',
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800',
      products: ['Bulk Disposables', 'Serving Items', 'Food Containers', 'Chafing Dishes', 'Packaging'],
      stats: { clients: '100+', years: '14+' }
    },
    {
      id: 'corporate',
      name: 'Corporate Offices',
      icon: Building,
      description: 'Office housekeeping supplies, pantry essentials, and hygiene products for corporate facilities and commercial spaces.',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
      products: ['Cleaning Supplies', 'Pantry Items', 'Paper Products', 'Hygiene Products', 'Safety Items'],
      stats: { clients: '75+', years: '10+' }
    },
  ];

  return (
    <div className="min-h-screen" data-testid="industries-page">
      <SEO title="Industries Served" description="Serving hotels, restaurants, airlines, catering companies, and corporate offices with premium hospitality supplies across India." />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#FAFAF9]" data-testid="industries-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="section-overline text-[#D4AF37]">Industries We Serve</span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mt-4"
            style={{ fontFamily: 'Manrope' }}
          >
            Empowering Hospitality<br />
            <span className="text-[#D4AF37]">Across Industries</span>
          </h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
            From hotels to airlines, we provide tailored solutions for diverse hospitality needs. 
            Our products serve thousands of businesses across India.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24 bg-white" data-testid="industries-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {industries.map((industry, idx) => (
              <div
                key={industry.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`industry-${industry.id}`}
              >
                {/* Image */}
                <div className={`relative ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="industry-card">
                    <img
                      src={industry.image}
                      alt={industry.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-8 z-10">
                      <div className="flex gap-8">
                        <div>
                          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                            {industry.stats.clients}
                          </p>
                          <p className="text-white/70 text-sm">Clients</p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                            {industry.stats.years}
                          </p>
                          <p className="text-white/70 text-sm">Years Serving</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                      <industry.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h2
                      className="text-3xl font-bold text-primary"
                      style={{ fontFamily: 'Manrope' }}
                    >
                      {industry.name}
                    </h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {industry.description}
                  </p>

                  {/* Products List */}
                  <div className="mb-8">
                    <p className="font-medium text-primary mb-3">Products we supply:</p>
                    <div className="flex flex-wrap gap-2">
                      {industry.products.map((product, pIdx) => (
                        <span
                          key={pIdx}
                          className="flex items-center gap-1 px-3 py-1.5 bg-muted rounded-full text-sm text-muted-foreground"
                        >
                          <Check className="w-3 h-3 text-primary" />
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Link to="/products">
                      <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid={`${industry.id}-products-btn`}>
                        View Products
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/contact">
                      <Button variant="outline" className="rounded-full border-primary text-primary" data-testid={`${industry.id}-quote-btn`}>
                        Get Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section py-24 md:py-32" data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Your Industry Not Listed?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            We serve many more industries with customized hospitality solutions. 
            Contact us to discuss your specific requirements.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/90 px-8 py-6 font-semibold"
              data-testid="industries-contact-btn"
            >
              Contact Us Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
